import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  MessageSquare,
  Send,
  Check,
  CheckCheck,
  ArrowLeft,
  Users,
  Plus,
  Info
} from 'lucide-react'
import { Card, CardBody } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Avatar } from '@/components/ui/Avatar'
import { messageService } from '@/services/messageService'
import { groupService } from '@/services/groupService'
import { useAuth } from '@/contexts/Auth0Context'
import { useToast } from '@/contexts/ToastContext'
import { useMessagingSocket } from '@/contexts/MessagingSocketContext'
import { Message, Conversation } from '@/types/message'
import { GroupRoom } from '@/types/group'
import {
  MessagingSocketEvent,
  INotificationUnreadPayload,
  IRoomDissolvedPayload
} from '@/types/messaging'
import CreateGroupModal from '@/components/messages/CreateGroupModal'
import NewMessageModal from '@/components/messages/NewMessageModal'
import GroupChatView from '@/components/messages/GroupChatView'
import GroupInfoPanel from '@/components/messages/GroupInfoPanel'

type InboxItem =
  | { kind: 'conversation'; lastMessageAt: string; data: Conversation }
  | { kind: 'group'; lastMessageAt: string; data: GroupRoom }

const MessagesPage: React.FC = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth()
  const { showToast } = useToast()
  const { on } = useMessagingSocket()
  const [searchParams, setSearchParams] = useSearchParams()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [groups, setGroups] = useState<GroupRoom[]>([])
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [selectedGroup, setSelectedGroup] = useState<GroupRoom | null>(null)
  const selectedGroupIdRef = useRef<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [sending, setSending] = useState(false)
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false)
  const [isNewMessageOpen, setIsNewMessageOpen] = useState(false)
  const [isGroupInfoOpen, setIsGroupInfoOpen] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Prevent duplicate API calls
  const fetchingConversationsRef = useRef(false)

  const inboxItems = useMemo<InboxItem[]>(() => {
    const items: InboxItem[] = [
      ...conversations.map((c): InboxItem => ({
        kind: 'conversation',
        lastMessageAt: c.lastMessage?.createdAt || '',
        data: c
      })),
      ...groups.map((g): InboxItem => ({
        kind: 'group',
        lastMessageAt: g.lastMessageAt || g.createdAt,
        data: g
      }))
    ]
    return items.sort(
      (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
    )
  }, [conversations, groups])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const fetchConversations = useCallback(async () => {
    if (fetchingConversationsRef.current) {
      return
    }

    try {
      fetchingConversationsRef.current = true
      setLoading(true)
      const data = await messageService.getConversations()
      setConversations(Array.isArray(data) ? data : [])
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to fetch conversations'
      })
    } finally {
      setLoading(false)
      fetchingConversationsRef.current = false
    }
  }, [showToast])

  const fetchGroups = useCallback(async () => {
    try {
      const data = await groupService.getGroups()
      setGroups(Array.isArray(data) ? data : [])
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to fetch groups'
      })
    }
  }, [showToast])

  const fetchMessages = useCallback(async (relationshipId: string) => {
    try {
      setLoadingMessages(true)
      const response = await messageService.getConversation(relationshipId)
      // Handle both array response and object with messages property
      const messagesData = Array.isArray(response) ? response : (response?.messages || [])
      // The API returns newest-first (for pagination); reverse for display so
      // the oldest message is at the top and the newest is at the bottom.
      setMessages([...messagesData].reverse())

      // Mark conversation as read
      await messageService.markConversationAsRead(relationshipId)

      // Update unread count in conversation list
      setConversations(prev =>
        prev.map(conv =>
          conv.relationshipId === relationshipId
            ? { ...conv, unreadCount: 0 }
            : conv
        )
      )
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to fetch messages'
      })
    } finally {
      setLoadingMessages(false)
    }
  }, [showToast])

  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      if (!authLoading && !isAuthenticated) {
        setLoading(false)
      }
      return
    }

    fetchConversations()
    fetchGroups()
  }, [authLoading, isAuthenticated, fetchConversations, fetchGroups])

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.relationshipId)
    }
  }, [selectedConversation, fetchMessages])

  useEffect(() => {
    return on<INotificationUnreadPayload>(MessagingSocketEvent.NOTIFICATION_UNREAD, (payload) => {
      setGroups(prev =>
        prev.map(g => (g.id === payload.roomId ? { ...g, unreadCount: payload.unreadCount } : g))
      )
    })
  }, [on])

  useEffect(() => {
    selectedGroupIdRef.current = selectedGroup?.id ?? null
  }, [selectedGroup])

  useEffect(() => {
    return on<IRoomDissolvedPayload>(MessagingSocketEvent.ROOM_DISSOLVED, (payload) => {
      setGroups(prev => prev.filter(g => g.id !== payload.roomId))
      if (selectedGroupIdRef.current === payload.roomId) {
        showToast({ type: 'info', message: 'This group was dissolved.' })
        setSelectedConversation(null)
        setSelectedGroup(null)
        setMessages([])
        setIsGroupInfoOpen(false)
      }
    })
  }, [on, showToast])

  // Deep-link support: /messages?relationshipId=xxx opens that conversation
  useEffect(() => {
    if (loading) return
    const relationshipId = searchParams.get('relationshipId')
    if (!relationshipId) return

    const conversation = conversations.find((c) => c.relationshipId === relationshipId)
    if (conversation) {
      setSelectedGroup(null)
      setSelectedConversation(conversation)
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('relationshipId')
      return next
    }, { replace: true })
  }, [loading, conversations, searchParams, setSearchParams])

  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedGroup(null)
    setSelectedConversation(conversation)
  }

  const handleSelectGroup = (group: GroupRoom) => {
    setSelectedConversation(null)
    setMessages([])
    setSelectedGroup({ ...group, unreadCount: 0 })
    setGroups(prev => prev.map(g => (g.id === group.id ? { ...g, unreadCount: 0 } : g)))
    setIsGroupInfoOpen(false)
  }

  const handleGroupCreated = (group: GroupRoom) => {
    setGroups(prev =>
      prev.some(g => g.id === group.id)
        ? prev.map(g => (g.id === group.id ? group : g))
        : [group, ...prev]
    )
    handleSelectGroup(group)
  }

  const handleBackToList = () => {
    setSelectedConversation(null)
    setSelectedGroup(null)
    setMessages([])
    setIsGroupInfoOpen(false)
  }

  const handleGroupLeft = () => {
    if (selectedGroup) {
      setGroups(prev => prev.filter(g => g.id !== selectedGroup.id))
    }
    handleBackToList()
  }

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!newMessage.trim() || !selectedConversation) {
      return
    }

    try {
      setSending(true)
      const message = await messageService.sendMessage({
        relationshipId: selectedConversation.relationshipId,
        receiverId: selectedConversation.participant.id,
        content: newMessage.trim()
      })

      setMessages(prev => [...prev, message])
      setNewMessage('')

      // Update last message in conversation list
      setConversations(prev =>
        prev.map(conv =>
          conv.relationshipId === selectedConversation.relationshipId
            ? { ...conv, lastMessage: message }
            : conv
        )
      )
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to send message'
      })
    } finally {
      setSending(false)
    }
  }

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    } else if (diffDays === 1) {
      return 'Yesterday'
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' })
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    })
  }

  const formatMessageTime = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const renderConversationRow = (conversation: Conversation) => (
    <button
      key={`conversation-${conversation.relationshipId}`}
      data-testid="inbox-conversation-row"
      onClick={() => handleSelectConversation(conversation)}
      className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
        selectedConversation?.relationshipId === conversation.relationshipId
          ? 'bg-primary-50'
          : ''
      }`}
    >
      <div className="flex items-center gap-3">
        <Avatar
          src={conversation.participant.profilePicture}
          name={`${conversation.participant.firstName} ${conversation.participant.lastName}`}
          size="md"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900 truncate">
              {conversation.participant.firstName} {conversation.participant.lastName}
            </h3>
            {conversation.lastMessage && (
              <span className="text-xs text-gray-500">
                {formatTime(conversation.lastMessage.createdAt)}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500 truncate">
              {conversation.lastMessage?.content || 'No messages yet'}
            </p>
            {conversation.unreadCount > 0 && (
              <span className="ml-2 bg-primary-600 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                {conversation.unreadCount}
              </span>
            )}
          </div>
          <span className="text-xs text-gray-400 capitalize">
            {conversation.participant.role}
          </span>
        </div>
      </div>
    </button>
  )

  const renderGroupRow = (group: GroupRoom) => {
    const isDirect = group.type === 'direct'
    const other = group.members?.[0]
    const title = isDirect && other ? `${other.firstName} ${other.lastName}` : group.name

    return (
      <button
        key={`group-${group.id}`}
        data-testid="inbox-group-row"
        onClick={() => handleSelectGroup(group)}
        className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
          selectedGroup?.id === group.id ? 'bg-primary-50' : ''
        }`}
      >
        <div className="flex items-center gap-3">
          {isDirect ? (
            <Avatar src={other?.profilePicture} name={title} size="md" />
          ) : (
            <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
              <Users className="w-6 h-6 text-accent-700" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-900 truncate">{title}</h3>
              {group.lastMessageAt && (
                <span className="text-xs text-gray-500">{formatTime(group.lastMessageAt)}</span>
              )}
            </div>
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500 truncate">
                {isDirect ? 'Direct message' : group.description || `${group.memberIds.length} members`}
              </p>
              {!!group.unreadCount && group.unreadCount > 0 && (
                <span className="ml-2 bg-primary-600 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                  {group.unreadCount}
                </span>
              )}
            </div>
            <span className="text-xs text-gray-400">{isDirect ? 'Direct message' : 'Group'}</span>
          </div>
        </div>
      </button>
    )
  }

  const renderConversationList = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      )
    }

    if (inboxItems.length === 0) {
      return (
        <div className="text-center py-12 px-4">
          <MessageSquare className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No conversations yet</h3>
          <p className="text-sm text-gray-500">
            Your messages and groups will appear here
          </p>
        </div>
      )
    }

    return (
      <div className="divide-y divide-gray-200">
        {inboxItems.map((item) =>
          item.kind === 'conversation'
            ? renderConversationRow(item.data)
            : renderGroupRow(item.data)
        )}
      </div>
    )
  }

  const renderMessageThread = () => {
    if (!selectedConversation) return null

    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-gray-200 bg-white">
          <button
            onClick={handleBackToList}
            className="lg:hidden p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <Avatar
            src={selectedConversation.participant.profilePicture}
            name={`${selectedConversation.participant.firstName} ${selectedConversation.participant.lastName}`}
            size="sm"
          />
          <div>
            <h2 className="font-bold text-gray-900">
              {selectedConversation.participant.firstName}{' '}
              {selectedConversation.participant.lastName}
            </h2>
            <span className="text-xs text-gray-500 capitalize">
              {selectedConversation.participant.role}
            </span>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {loadingMessages ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((message) => {
              const isOwnMessage = message.senderId === user?.id
              return (
                <div
                  key={message.id}
                  className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] px-4 py-2 ${
                      isOwnMessage
                        ? 'bg-primary-500 text-white rounded-2xl rounded-tr-md'
                        : 'bg-white text-gray-900 border border-gray-200 rounded-2xl rounded-tl-md'
                    }`}
                  >
                    <p className="break-words">{message.content}</p>
                    <div
                      className={`flex items-center justify-end gap-1 mt-1 text-xs ${
                        isOwnMessage ? 'text-primary-200' : 'text-gray-400'
                      }`}
                    >
                      <span>{formatMessageTime(message.createdAt)}</span>
                      {isOwnMessage && (
                        message.isRead ? (
                          <CheckCheck className="w-3.5 h-3.5" />
                        ) : (
                          <Check className="w-3.5 h-3.5" />
                        )
                      )}
                    </div>
                  </div>
                </div>
              )
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 bg-white">
          <div className="flex items-center gap-2">
            <Input
              type="text"
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              className="flex-1"
              disabled={sending}
            />
            <Button
              type="submit"
              variant="primary"
              disabled={!newMessage.trim() || sending}
              isLoading={sending}
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </form>
      </div>
    )
  }

  const renderGroupPanel = () => {
    if (!selectedGroup) return null

    const isDirect = selectedGroup.type === 'direct'
    const other = selectedGroup.members?.[0]
    const title = isDirect && other ? `${other.firstName} ${other.lastName}` : selectedGroup.name

    return (
      <div className="flex h-full">
        <div className="flex flex-col h-full flex-1 min-w-0">
          <div className="flex items-center gap-3 p-4 border-b border-gray-200 bg-white">
            <button
              onClick={handleBackToList}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            {isDirect ? (
              <Avatar src={other?.profilePicture} name={title} size="sm" />
            ) : (
              <div className="w-10 h-10 bg-accent-100 rounded-full flex items-center justify-center overflow-hidden">
                <Users className="w-5 h-5 text-accent-700" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-gray-900">{title}</h2>
              <span className="text-xs text-gray-500">
                {isDirect ? 'Direct message' : `${selectedGroup.memberIds.length} members`}
              </span>
            </div>
            {!isDirect && (
              <button
                onClick={() => setIsGroupInfoOpen((prev) => !prev)}
                aria-label="Group info"
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <Info className="w-5 h-5 text-gray-600" />
              </button>
            )}
          </div>
          <div className="flex-1 min-h-0">
            <GroupChatView group={selectedGroup} />
          </div>
        </div>
        {!isDirect && isGroupInfoOpen && user && (
          <GroupInfoPanel
            group={selectedGroup}
            currentUserId={user.id}
            currentUserRole={user.role}
            isOpen={isGroupInfoOpen}
            onClose={() => setIsGroupInfoOpen(false)}
            onLeaveGroup={handleGroupLeft}
            onDissolveGroup={handleGroupLeft}
          />
        )}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-display text-gray-900">Messages</h1>
            <p className="mt-2 text-gray-600">
              Connect with your mentors, mentees, and groups
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setIsNewMessageOpen(true)}>
              <Plus className="w-4 h-4 mr-1" />
              New Message
            </Button>
            <Button variant="primary" onClick={() => setIsCreateGroupOpen(true)}>
              <Plus className="w-4 h-4 mr-1" />
              New Group
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <Card className="overflow-hidden">
          <CardBody className="p-0">
            <div className="flex h-[600px]">
              {/* Conversation List */}
              <div
                className={`w-full lg:w-1/3 border-r border-gray-200 bg-white ${
                  selectedConversation || selectedGroup ? 'hidden lg:block' : 'block'
                }`}
              >
                <div className="p-4 border-b border-gray-200">
                  <h2 className="font-bold text-gray-900">Conversations</h2>
                </div>
                <div className="overflow-y-auto h-[calc(100%-57px)]">
                  {renderConversationList()}
                </div>
              </div>

              {/* Message Thread */}
              <div
                className={`w-full lg:w-2/3 ${
                  selectedConversation || selectedGroup ? 'block' : 'hidden lg:flex'
                } lg:flex-col`}
              >
                {selectedConversation ? (
                  renderMessageThread()
                ) : selectedGroup ? (
                  renderGroupPanel()
                ) : (
                  <div className="flex-1 flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                      <MessageSquare className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        Select a conversation
                      </h3>
                      <p className="text-gray-500">
                        Choose a conversation from the list to start messaging
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      <CreateGroupModal
        isOpen={isCreateGroupOpen}
        onClose={() => setIsCreateGroupOpen(false)}
        onGroupCreated={handleGroupCreated}
      />

      <NewMessageModal
        isOpen={isNewMessageOpen}
        onClose={() => setIsNewMessageOpen(false)}
        onConversationStarted={handleGroupCreated}
      />
    </div>
  )
}

export default MessagesPage
